import { useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, CheckCircle2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";

export default function AccountDeletion() {
  const [identifier, setIdentifier] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/account-deletion", { identifier });
      setDone({ matched: data.matched });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[70vh] max-w-2xl mx-auto px-6 py-20 text-center allow-select" data-testid="deletion-confirmation">
        <CheckCircle2 className="h-14 w-14 text-gold mx-auto mb-5" />
        <h1 className="font-display text-3xl font-bold text-navy">Deletion request received</h1>
        <p className="mt-4 text-slate-600">
          {done.matched
            ? "We found a matching account and have permanently deleted it and its associated data."
            : "We've recorded your request. If a matching account exists, it will be removed."}
        </p>
        <p className="mt-2 text-sm text-slate-500">A copy of your request has been emailed to our team. Any remaining processing completes within <strong>7 days</strong>.</p>
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="account-deletion-page">
      <div className="bg-navy grain">
        <div className="max-w-2xl mx-auto px-6 py-14">
          <p className="eyebrow text-gold">Privacy</p>
          <h1 className="font-display text-4xl font-bold text-white mt-2">Delete My Account</h1>
          <p className="mt-3 text-slate-400">Request permanent deletion of your KRV Prime account and all associated data.</p>
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-6 py-12 allow-select">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex gap-2">
          <ShieldAlert className="h-5 w-5 shrink-0" />
          <span>This permanently deletes your <strong>profile, name, email, mobile number, profile photo, saved addresses, bookmarks and learning activity</strong>. This action cannot be undone.</span>
        </div>
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="deletion-form">
          <label className="block text-sm font-medium text-navy">Registered email or mobile number</label>
          <input required value={identifier} onChange={(e) => setIdentifier(e.target.value)} data-testid="deletion-identifier" placeholder="you@example.com or 9876543210" className="w-full h-12 rounded-md border border-border px-4 text-sm outline-none focus:ring-2 focus:ring-gold" />
          <button type="submit" disabled={loading} data-testid="deletion-submit" className="w-full h-12 rounded-md bg-red-600 text-white font-semibold text-sm hover:bg-red-700 transition-colors disabled:opacity-60">
            {loading ? "Submitting…" : "Request account deletion"}
          </button>
        </form>
        <p className="mt-6 text-xs text-slate-500">Requests are processed within <strong>7 days</strong>. Data required for legal, tax or fraud-prevention purposes may be retained only as long as legally required, after which it is deleted. A copy of your request is emailed to contact@krvprime.com.</p>
      </div>
    </div>
  );
}
